import { h } from 'preact';
import { useThreekit } from '../threekit-context';

const ClickSelection = ({
  children,
  element,
  className,
  variant,
  configurationValue,
  attributeName,
  value,
  additionalOnClick,
}) => {
  const {
    configurator,
    setConfiguration,
    configuration,
    bladeSide,
    getProductsFromAttribute,
    setFrontImage,
    setBackImage,
    frontImage,
    setFrontCustomImage,
    setBackCustomImage,
    setFrontTextLine1,
    setFrontTextLine2,
    setBackTextLine1,
    setBackTextLine2,
    bladeColorDuplicateSide,
  } = useThreekit();
  const Component = element || 'button';

  // A method to handle the following scenario where the oppsite color
  // might not have a color associated with the material
  const handleMaterialChange = (configurations) => {
    const oppositeBladeSide = bladeSide === 'Front' ? 'Back' : 'Front';

    const handleProducts = getProductsFromAttribute(
      `${oppositeBladeSide} Handle Color`,
    );
    const hasColor = handleProducts.every((product) =>
      product.variants.edges.some((variant) => {
        const color = variant.node.configuration[`${oppositeBladeSide} Handle Color`]
          ?.assetId;
        const configColor = configuration[`${oppositeBladeSide} Handle Color`].assetId;
        return color === configColor;
      }),
    );

    if (!hasColor) {
      const colorConfig = configurations[
        Object.keys(configurations).find((key) =>
          key.includes('Handle Color'),
        )
      ];

      return {
        [`${oppositeBladeSide} Handle Color`]: colorConfig,
      };
    }

    return null;
  };

  const handleButtonClick = async () => {
    if (additionalOnClick) {
      additionalOnClick();
    }
    if (variant) {
      let configurations = variant.node.configuration;

      // if we changed materials, we need to check that
      // the opposite-sided color exists for the material
      if (
        Object.keys(configurations).includes('Handle Material')
        && configurations['Handle Material'].assetId
          !== configuration['Handle Material'].assetId
      ) {
        const materialChange = handleMaterialChange(configurations);
        configurations = { ...configurations, ...materialChange };
      }

      // logic for duplicating blade colors for both sides of the knife
      if (
        Object.keys(configurations).includes(`${bladeSide} Handle Color`)
        && bladeColorDuplicateSide
      ) {
        configurations = {
          ...configurations,
          'Front Handle Color': configurations[`${bladeSide} Handle Color`],
          'Back Handle Color': configurations[`${bladeSide} Handle Color`],
        };
      }

      // If this selection hides another variant, remove it from the config
      if (variant.node.hideOnSelection) {
        const categories = JSON.parse(variant.node.hideOnSelection.value);
        categories.forEach(async (category) => {
          // FIXME: This should utilize a more reliable way of mapping categories & config keys
          if (category === 'Bolster') {
            await configurator.setConfiguration({
              'Bolster Color': '',
            });
          } else if (category === 'Artwork') {
            await configurator.setConfiguration({
              'Add Front Image': '',
              'Add Back Image': '',
              'Upload Front Image': '',
              'Upload Back Image': '',
              'Add Front Pattern': '',
              'Add Back Pattern': '',
              'Choose Custom Art': '',
            });
            setFrontCustomImage(false);
            setBackCustomImage(false);
            setFrontImage('');
            setBackImage('');
          } else if (category === 'Text') {
            await configurator.setConfiguration({
              'Add Front Text': '',
              'Add Front Text Line_2': '',
              'Add Back Text': '',
              'Add Back Text Line_2': '',
            });
            setFrontTextLine1('');
            setFrontTextLine2('');
            setBackTextLine1('');
            setBackTextLine2('');
          } else if (category === 'Shape') {
            await configurator.setConfiguration({
              'Blade Design':
                configurator.getConfiguration()['Blade Design'].defaultValue,
            });
          }
        });
      }

      // Assign Handle Material first to avoid issues with Handle Color
      if (configurations['Handle Material']) {
        await configurator.setConfiguration({
          'Handle Material': configurations['Handle Material'],
        });
      }
      // Assign Blade Steel first to avoid issues with Blade Color
      if (configurations['Blade Steel']) {
        await configurator.setConfiguration({
          'Blade Steel': configurations['Blade Steel'],
        });
      }

      await configurator.setConfiguration(configurations);
      setConfiguration(configurator.getConfiguration());
      return;
    }

    if (configurationValue) {
      await configurator.setConfiguration(configurationValue);
      setConfiguration(configurator.getConfiguration());
      return;
    }

    if (attributeName === 'Choose Custom Art') {
      await configurator.setConfiguration({
        'Add Front Image': '',
        'Add Back Image': '',
        'Upload Front Image': '',
        'Upload Back Image': '',
        'Add Front Pattern': '',
        'Add Back Pattern': '',
        'Add Front Text': '',
        'Add Front Text Line_2': '',
        'Add Back Text': '',
        'Add Back Text Line_2': '',
        [attributeName]: value,
      });
      setConfiguration({
        ...configuration,
        'Add Front Image': '',
        'Add Back Image': '',
        'Upload Front Image': '',
        'Upload Back Image': '',
        'Add Front Pattern': '',
        'Add Back Pattern': '',
        'Add Front Text': '',
        'Add Back Text': '',
        'Add Front Text Line_2': '',
        'Add Back Text Line_2': '',
        attributeName: value,
      });
    }

    if (
      attributeName === `Add ${bladeSide} Image`
      || attributeName === `Upload ${bladeSide} Image`
      || attributeName === `Add ${bladeSide} Pattern`
      || attributeName === 'Stock Image'
    ) {
      if (bladeSide === 'Front') {
        await configurator.setConfiguration({
          'Add Front Image': '',
          'Upload Front Image': '',
          'Add Front Pattern': '',
          'Choose Custom Art': '',
        });
        setFrontCustomImage(false);
        if (value === null || value === '') {
          setFrontImage('');
        } else {
          setFrontImage(attributeName);
        }
      } else if (bladeSide === 'Back') {
        await configurator.setConfiguration({
          'Add Back Image': '',
          'Add Back Pattern': '',
          'Upload Back Image': '',
          'Choose Custom Art': '',
        });
        setBackCustomImage(false);
        if (value === null || value === '') {
          setBackImage('');
        } else {
          setBackImage(attributeName);
          if (frontImage === 'Choose Custom Art') {
            setFrontImage('');
          }
        }
      }
    } else if (attributeName === 'Choose Custom Art') {
      if (bladeSide === 'Front') {
        setFrontImage(value !== '' ? attributeName : '');
        setFrontCustomImage(false);
        setBackCustomImage(false);
        setFrontTextLine1('');
        setFrontTextLine2('');
        setBackImage('');
        setBackTextLine1('');
        setBackTextLine2('');
      }
    }

    // for 2D knives to set the Stock Image correctly
    if (attributeName === 'Stock Image') {
      await configurator.setConfiguration({ 'Upload Image': '' });
    }

    setConfiguration({ ...configuration, [attributeName]: value });
    await configurator.setConfiguration({ [attributeName]: value });
  };

  return (
    <Component className={className} onClick={() => handleButtonClick()}>
      {children}
    </Component>
  );
};

export default ClickSelection;
